import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';

const PersonalInfoStep = ({ formData, updateFormData, onNext }) => {
  const navigate = useNavigate();
  const [errors, setErrors] = useState({});

  const idTypes = [
    { value: 'CC', label: 'Cédula de Ciudadanía' },
    { value: 'CE', label: 'Cédula de Extranjería' },
    { value: 'TI', label: 'Tarjeta de Identidad' },
    { value: 'PA', label: 'Pasaporte' }
  ];

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.fullName?.trim()) {
      newErrors.fullName = 'El nombre completo es requerido';
    }

    if (!formData?.email?.trim()) {
      newErrors.email = 'El correo electrónico es requerido';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
      newErrors.email = 'Correo electrónico inválido';
    }

    if (formData?.email !== formData?.confirmEmail) {
      newErrors.confirmEmail = 'Los correos electrónicos no coinciden';
    }

    if (!formData?.phone?.trim()) {
      newErrors.phone = 'El número de teléfono es requerido';
    } else if (!/^(\+57)?[0-9]{10}$/?.test(formData?.phone?.replace(/\s/g, ''))) {
      newErrors.phone = 'Formato: +57 312 456 7890';
    }

    if (!formData?.idNumber?.trim()) {
      newErrors.idNumber = 'El número de identificación es requerido';
    }

    if (!formData?.password) {
      newErrors.password = 'La contraseña es requerida';
    } else if (formData?.password?.length < 8) {
      newErrors.password = 'Mínimo 8 caracteres';
    }

    if (formData?.password !== formData?.confirmPassword) {
      newErrors.confirmPassword = 'Las contraseñas no coinciden';
    }

    if (!formData?.acceptTerms) {
      newErrors.acceptTerms = 'Debes aceptar los términos y condiciones';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (validateForm()) {
      onNext();
    }
  };

  const formatPhoneNumber = (value) => {
    const cleaned = value?.replace(/\D/g, '');
    if (cleaned?.startsWith('57')) {
      const number = cleaned?.substring(2);
      if (number?.length <= 10) {
        return `+57 ${number?.substring(0, 3)} ${number?.substring(3, 6)} ${number?.substring(6, 10)}`?.trim();
      }
    } else if (cleaned?.length <= 10) {
      return `${cleaned?.substring(0, 3)} ${cleaned?.substring(3, 6)} ${cleaned?.substring(6, 10)}`?.trim();
    }
    return value;
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-foreground mb-2">Información Personal</h2>
        <p className="text-muted-foreground">Completa tus datos personales para crear tu cuenta</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Input
          label="Nombre Completo"
          required
          placeholder="Ej: Carlos Andrés Martínez"
          value={formData?.fullName || ''}
          onChange={(e) => updateFormData({ fullName: e?.target?.value })}
          error={errors?.fullName}
        />

        <Input
          label="Correo Electrónico"
          type="email"
          required
          placeholder="correo@ejemplo.com"
          value={formData?.email || ''}
          onChange={(e) => updateFormData({ email: e?.target?.value })}
          error={errors?.email}
        />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Input
          label="Confirmar Correo Electrónico"
          type="email"
          required
          placeholder="correo@ejemplo.com"
          value={formData?.confirmEmail || ''}
          onChange={(e) => updateFormData({ confirmEmail: e?.target?.value })}
          error={errors?.confirmEmail}
        />

        <Input
          label="Número de Teléfono"
          type="tel"
          required
          placeholder="+57 312 456 7890"
          value={formData?.phone || ''}
          onChange={(e) => {
            const formatted = formatPhoneNumber(e?.target?.value);
            updateFormData({ phone: formatted });
          }}
          error={errors?.phone}
          description="Formato: +57 XXX XXX XXXX"
        />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <Select
            label="Tipo de Identificación"
            required
            value={formData?.idType || 'CC'}
            onChange={(e) => updateFormData({ idType: e?.target?.value })}
            options={idTypes}
          />
        </div>

        <Input
          label="Número de Identificación"
          required
          placeholder="Ej: 1085234567"
          value={formData?.idNumber || ''}
          onChange={(e) => updateFormData({ idNumber: e?.target?.value?.replace(/\D/g, '') })}
          error={errors?.idNumber}
        />
      </div>
      <div className="border-t pt-6">
        <h3 className="text-lg font-bold text-foreground mb-4 flex items-center gap-2">
          <Icon name="Lock" size={20} className="text-primary" />
          Configuración de Seguridad
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Input
            label="Contraseña"
            type="password"
            required
            placeholder="Mínimo 8 caracteres"
            value={formData?.password || ''}
            onChange={(e) => updateFormData({ password: e?.target?.value })}
            error={errors?.password}
          />

          <Input
            label="Confirmar Contraseña"
            type="password"
            required
            placeholder="Repite tu contraseña"
            value={formData?.confirmPassword || ''}
            onChange={(e) => updateFormData({ confirmPassword: e?.target?.value })}
            error={errors?.confirmPassword}
          />
        </div>
      </div>
      <div className="border-t pt-6">
        <div className="flex items-start gap-3">
          <Input
            type="checkbox"
            checked={formData?.acceptTerms || false}
            onChange={(e) => updateFormData({ acceptTerms: e?.target?.checked })}
            className="mt-1"
          />
          <label className="text-sm text-muted-foreground">
            Acepto los{' '}
            <a href="/terminos" className="text-primary font-semibold hover:underline">
              Términos y Condiciones
            </a>{' '}
            y la{' '}
            <a href="/privacidad" className="text-primary font-semibold hover:underline">
              Política de Privacidad
            </a>{' '}
            de BIZZNET
          </label>
        </div>
        {errors?.acceptTerms && (
          <p className="text-sm text-destructive mt-2 flex items-center gap-1">
            <Icon name="AlertCircle" size={14} />
            {errors?.acceptTerms}
          </p>
        )}
      </div>
      <div className="border-t pt-6">
        <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4 mb-6">
          <div className="flex items-start gap-3">
            <Icon name="Info" size={20} className="text-blue-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-blue-900">
              <p className="font-semibold mb-1">Registro seguro y protegido</p>
              <p>Tus datos están protegidos con encriptación de nivel bancario</p>
            </div>
          </div>
        </div>

        <Button
          type="submit"
          variant="default"
          size="lg"
          fullWidth
          iconName="ArrowRight"
          iconPosition="right"
          className="bg-gradient-to-r from-primary to-accent hover:shadow-lg transform hover:scale-105 transition-all"
        >
          Continuar al Siguiente Paso
        </Button>
      </div>
      <div className="text-center pt-4 border-t">
        <p className="text-sm text-muted-foreground">
          ¿Ya tienes una cuenta?{' '}
          <button
            type="button"
            onClick={() => navigate('/user-login')}
            className="text-primary font-semibold hover:underline cursor-pointer bg-transparent border-none"
          >
            Inicia Sesión
          </button>
        </p>
      </div>
    </form>
  );
};

export default PersonalInfoStep;